import { QueryObserverResult, useQueries, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInitiatives } from '../../../api/initiative-client/initiative-client.hooks';
import type { Initiative } from '../../../api/initiative-client/initiative-client.type';
import type { ComponentData } from '../../../api/pages-client/pages-client.type';
import { useAvailableTargets } from '../../../api/targets-client/targets-client.hooks';
import type {
  GetAvailableTargetsPayload,
  GetAvailableTargetsResponse,
  Target,
} from '../../../api/targets-client/targets-client.type';
import { TargetComparison } from '../../../api/targets-client/targets-client.type';
import {
  getCustomSprintMeasure,
  getWorkPeriods,
  getWorkPeriodsMeasure,
} from '../../../api/work-periods-client/work-periods-client';
import { useAvailableMeasures } from '../../../api/work-periods-client/work-periods-client.hooks';
import type {
  AvailableMeasuresResponse,
  GetWorkPeriodsMeasurePayload,
  MeasureDataResponse,
  MeasureMetadata,
  MeasureUnits,
  WorkPeriod,
} from '../../../api/work-periods-client/work-periods-client.type';
import {
  Measure,
  ModuleType,
  TimeAllocationType,
  Transformer,
  WorkPeriodTense,
} from '../../../api/work-periods-client/work-periods-client.type';
import { useOrganizationId } from '../../../helpers/auth-helpers/auth.hooks';
import type { PhaseLabel } from '../../../helpers/phase-labels/phase-labels.type';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { getLastNotNullValue } from '../../../helpers/process-analysis-shared/process-analysis-shared.helpers';
import { formatMeasureName } from '../../process-analysis/process-analysis.helpers';
import {
  getComponentDateRange,
  getComponentNSprints,
  isCustomSprintMeasureDataResponse,
  selectSprintWorkPeriods,
} from '../dashboards.helpers';
import type {
  CumulativeFlowDataPoint,
  ProcessedWidgetData,
  SprintChartDataPoint,
  SprintData,
  TableWidgetDataPoint,
  WidgetDataPoint,
  WidgetFormData,
} from './dashboards.hooks.type';

/**
 * Calculates the time allocation based on the difference between the start and end dates.
 *
 * @param {TimeAllocationType} timeAllocationType - The time allocation type
 * @param {string} startDate - The start date
 * @param {string} endDate - The end date
 * @return {TimeAllocationType} The time allocation, which can be Daily, Weekly, BiWeekly, or Monthly.
 */
const determineTimeAllocation = (
  timeAllocationType: TimeAllocationType,
  startDate: string,
  endDate: string,
): TimeAllocationType => {
  if (timeAllocationType) {
    return timeAllocationType;
  }

  const diff = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;

  switch (true) {
    case diff < 25:
      return TimeAllocationType.Daily;
    case diff < 51:
      return TimeAllocationType.Weekly;
    case diff < 101:
      return TimeAllocationType.BiWeekly;
    default:
      return TimeAllocationType.Monthly;
  }
};

/**
 * Hook to fetch and process widget data
 *
 * @param {ComponentData} componentData - The component data to fetch data for
 * @param {string} pageId - The ID of the page
 * @returns {ProcessedWidgetData} - The processed widget data
 */
const useWidgetData = (componentData: ComponentData, pageId: string): ProcessedWidgetData => {
  const { t } = useTranslation();
  const portfolios = useGlobalStore((state) => state.portfolios);
  const teams = useGlobalStore((state) => state.teams);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const superuserMode = useGlobalStore((state) => state.superuserMode);
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const { query: initiativesQuery, initiatives = [] } = useInitiatives(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<Initiative[]>);
  const hasInitiatives = initiativesQuery.isSuccess && !!initiatives.length;

  // Fetch available measures to get measure_units metadata for Y-axis labels
  const isSprint = componentData.object_type === 'sprint';
  const measuresScope = isSprint ? [ModuleType.WorkPeriod] : [ModuleType.ProcessAnalysis, ModuleType.Dashboard];
  const firstObjectId = componentData.object_ids[0];
  const { data: availableMeasuresData } = useAvailableMeasures({
    payload: {
      scope: measuresScope,
      portfolio_id: componentData.object_type === 'portfolio' ? firstObjectId : undefined,
      project_id: componentData.object_type === 'project' ? firstObjectId : undefined,
      subproject_id: componentData.object_type === 'board' || isSprint ? firstObjectId : undefined,
    },
    options: {
      enabled: !!firstObjectId && !!organizationId,
      queryKey: ['available-measures', componentData.object_type, firstObjectId, measuresScope],
    },
  });

  const _measureUnits = useMemo(() => {
    if (!availableMeasuresData) return undefined;
    const measures = componentData.measure ? componentData.measure.split(',').map((m) => m.trim()) : [];
    const allMeasureMetadata: MeasureMetadata[] = [];
    for (const s of measuresScope) {
      const scopeMeasures = availableMeasuresData[s]?.measures;
      if (scopeMeasures) {
        allMeasureMetadata.push(...scopeMeasures);
      }
    }
    const units: string[] = measures
      .map((m) => allMeasureMetadata.find((meta) => meta.measure_name === m)?.measure_units)
      .filter((u): u is MeasureUnits => !!u);
    const uniqueUnits = Array.from(new Set(units));
    return uniqueUnits.length > 0 ? uniqueUnits : undefined;
  }, [availableMeasuresData, componentData.measure, measuresScope]);

  /**
   * Resolves the display title for a raw measure key (e.g. "sprint_throughput"),
   * preferring the API-provided title from available-measures metadata and
   * falling back to the i18n/Title-Case formatter when metadata for the key
   * hasn't loaded yet or doesn't exist. Prevents chart legends from showing
   * internal metric keys to the user.
   *
   * @param {string} measure - Raw measure key returned by the data API
   * @returns {string} Human-readable title for the measure
   */
  const getMeasureTitle = (measure: string): string => {
    for (const scope of measuresScope) {
      const found = availableMeasuresData?.[scope]?.measures?.find(
        (measureMetadata) => measureMetadata.measure_name === measure,
      );
      if (found) return found.measure_title;
    }
    return formatMeasureName(measure, t);
  };

  const boardNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (teams || []).forEach((team) => {
      team.subprojects.forEach((board) => {
        map.set(board.id, `${team.name} - ${board.name}`);
      });
    });
    return map;
  }, [teams]);

  // Helper function to get object name
  const getObjectName = (objectId: string, objectType: string): string => {
    switch (objectType) {
      case 'portfolio':
        return portfolios.find((p: any) => p.id === objectId)?.name || objectId;
      case 'project':
        return teams.find((t) => t.id === objectId)?.name || objectId;
      case 'board':
        return boardNameMap.get(objectId) || objectId;
      case 'sprint':
        // For sprint widgets, objectId is the board ID, so look up the board name
        return boardNameMap.get(objectId) || objectId;
      case 'initiative':
        return hasInitiatives ? initiatives.find((i) => i.id === objectId)?.name || objectId : objectId;
      default:
        return objectId;
    }
  };

  const { startDate, endDate } = getComponentDateRange(componentData);

  // Parse measures (can be comma-separated)
  const measures = componentData.measure ? componentData.measure.split(',').filter((m) => m.trim()) : [];
  const primaryMeasure = measures[0] || '';

  // Check if this is a special sprint chart measure
  const isSpecialSprintMeasure =
    componentData.object_type === 'sprint' &&
    (primaryMeasure === Measure.Burns ||
      primaryMeasure === Measure.FlowByPace ||
      primaryMeasure === Measure.FlowByPhase);

  // Create query configurations - one query per object per measure combination
  const queryConfigs = componentData.object_ids.flatMap((objectId) => {
    // For multiple measures, create separate queries for each measure
    return measures.map((measure) => {
      const isCustom =
        !Object.values(Measure).includes(measure as Measure) ||
        measure === Measure.Complexity ||
        measure === Measure.Readiness;
      const payload: GetWorkPeriodsMeasurePayload = {
        page_id: pageId,
        start_date: startDate,
        end_date: endDate,
        measure_name: [measure as Measure],
        force_recalculation: false,
        is_custom: isCustom,
        time_allocation_type: determineTimeAllocation(
          componentData.time_allocation_type as TimeAllocationType,
          startDate,
          endDate,
        ),
        organization_id: organizationId || undefined,
      };
      if (isCustom) {
        payload.transformer = Transformer.ReturnFirstMetricOnly;
      }

      // Set the appropriate ID based on object type
      switch (componentData.object_type) {
        case 'project':
          payload.project_id = objectId;
          break;
        case 'board':
          payload.subproject_id = objectId;
          break;
        case 'portfolio':
          payload.portfolio_id = objectId;
          break;
        case 'initiative':
          payload.initiative_id = objectId;
          break;
        case 'sprint': {
          // For special sprint measures (burns, flow_by_pace, flow_by_phase),
          // we need to fetch work periods first, then fetch data for each work period using work_period_id
          // Check if THIS measure (not just the primary measure) is special
          const isThisMeasureSpecial =
            measure === Measure.Burns || measure === Measure.FlowByPace || measure === Measure.FlowByPhase;

          if (isThisMeasureSpecial) {
            // First, we need to fetch the work periods to get their IDs
            // This will be handled in the query function
            const specialQueryConfig = {
              queryKey: [
                'widget-sprint-periods-measure',
                objectId,
                componentData.measure,
                getComponentNSprints(componentData),
                componentData.include_current_sprint ?? false,
                organizationId,
              ] as const,
              queryFn: async () => {
                // Step 1: Fetch the candidate sprints for this board. When the
                // active sprint is included we omit the tense filter so PRESENT
                // periods come back too; selectSprintWorkPeriods does the final
                // filtering, ordering, and slicing.
                const nSprints = getComponentNSprints(componentData);
                const includeCurrentSprint = componentData.include_current_sprint ?? false;
                const workPeriods = await getWorkPeriods({
                  subproject_id: objectId,
                  time_tense: includeCurrentSprint ? undefined : WorkPeriodTense.Past,
                  organization_id: organizationId || undefined,
                });

                const sortedSprints = selectSprintWorkPeriods(workPeriods, nSprints, includeCurrentSprint);

                // Step 2: Fetch measure data for each sprint using work_period_id
                const sprintDataPromises = sortedSprints.map(async (sprint) => {
                  const measureData = await getWorkPeriodsMeasure({
                    work_period_id: sprint.id,
                    subproject_id: objectId,
                    measure_name: [primaryMeasure as Measure],
                    page_id: pageId,
                    force_recalculation: false,
                    is_custom: false, // Use async-measure endpoint
                    time_allocation_type: TimeAllocationType.Daily,
                    module_type: ModuleType.WorkPeriod,
                    organization_id: organizationId || undefined,
                  });

                  return {
                    workPeriod: sprint,
                    data: measureData,
                  };
                });

                const results = await Promise.all(sprintDataPromises);

                // Return in the format expected by the data processing logic
                return {
                  sprints: results,
                  measure: primaryMeasure,
                };
              },
              retry: false,
              meta: { objectId, measure }, // Add metadata for data processing
              enabled: !!organizationId,
            };
            return specialQueryConfig;
          }
          // For other custom sprint measures, use the custom-sprint-measure endpoint
          const regularSprintConfig = {
            queryKey: [
              'widget-measure',
              objectId,
              componentData.object_type,
              measure, // Use measure from the iteration, not primaryMeasure
              getComponentNSprints(componentData),
              componentData.include_current_sprint ?? false,
              organizationId,
            ] as const,
            queryFn: () =>
              getCustomSprintMeasure({
                subproject_id: objectId,
                measure_name: measure, // Use measure from the iteration, not primaryMeasure
                n_sprints: getComponentNSprints(componentData),
                include_current_sprint: componentData.include_current_sprint ?? false,
                transformer: Transformer.ReturnFirstMetricOnly,
                module_type: ModuleType.WorkPeriod,
                organization_id: organizationId || undefined,
              }),
            retry: false,
            meta: { objectId, measure }, // Add metadata for data processing
            enabled: !!organizationId,
          };
          return regularSprintConfig;
        }
      }

      return {
        queryKey: [
          'widget-measure',
          objectId,
          measure,
          pageId,
          componentData.object_type,
          startDate,
          endDate,
          payload.time_allocation_type,
          organizationId,
        ] as const,
        queryFn: () =>
          getWorkPeriodsMeasure<MeasureDataResponse>({
            ...payload,
            superuser_force_recalculation: superuserMode.enabled ? superuserMode.forceRecalculation : false,
          }),
        retry: false,
        meta: { objectId, measure }, // Add metadata to help with data processing
        enabled: !!organizationId,
      };
    });
  });

  // Use useQueries hook
  const queries = useQueries({ queries: queryConfigs });

  // Check if any queries are loading
  const isLoading = queries.some((query) => query.isLoading);

  // Check for errors
  const error = queries.find((query) => query.error)?.error || null;

  // Helper function to transform burns data for charts
  const transformBurnsData = (data: any): SprintChartDataPoint[] => {
    if (!data || typeof data !== 'object') return [];

    return Object.entries(data).map(([date, value]: [string, any]) => ({
      date,
      scope: value.tasks_total || value.points_total || 0,
      tasks_total: value.tasks_total || 0,
      points_total: value.points_total || 0,
      tasks_completed: value.tasks_complete || 0,
      points_completed: value.points_complete || 0,
      // The burns measure does not emit per-metric remaining; leave it undefined
      // (not 0) so the chart's `??` fallback resolves to the derived `remaining`
      // below instead of treating an absent value as a real zero.
      tasks_remaining: value.tasks_remaining,
      points_remaining: value.points_remaining,
      // Use completed for backward compatibility
      completed: value.tasks_complete || value.points_complete || 0,
      remaining: (value.tasks_total || value.points_total || 0) - (value.tasks_complete || value.points_complete || 0),
      // Backend ideal (cumulative ideal-completed pace). Burn-down derives its
      // "Ideal Remaining" line from scope − ideal. Present once the burns measure
      // enables `output_ideals`; left undefined for older payloads so the chart
      // falls back to a linear ideal rather than a flat line.
      tasks_ideal: value.tasks_ideal,
      points_ideal: value.points_ideal,
      weekendOrHoliday: value.weekend_or_holiday || false,
    }));
  };

  // Helper function to transform flow by pace data for charts
  const transformFlowByPaceData = (data: any): SprintChartDataPoint[] => {
    if (!data || typeof data !== 'object') return [];

    return Object.entries(data).map(([date, value]: [string, any]) => ({
      date,
      scope: value.tasks_total || value.points_total || 0,
      tasks_total: value.tasks_total || 0,
      points_total: value.points_total || 0,
      tasks_completed: value.tasks_complete || 0,
      points_completed: value.points_complete || 0,
      tasks_remaining: value.tasks_remaining || 0,
      points_remaining: value.points_remaining || 0,
      // Use completed for backward compatibility
      completed: value.tasks_complete || value.points_complete || 0,
      remaining: value.tasks_remaining || value.points_remaining || 0,
      idealRemaining: value.tasks_ideal ?? value.points_ideal ?? 0,
      // Preserve both metrics' backend ideal separately so the burn chart can
      // switch between tasks and points (the single `idealRemaining` above
      // collapses them and is kept only for backward compatibility). Left
      // undefined when absent so the chart can fall back rather than flat-line.
      tasks_ideal: value.tasks_ideal,
      points_ideal: value.points_ideal,
      weekendOrHoliday: value.weekend_or_holiday || false,
    }));
  };

  // Helper function to transform flow by phase data for charts
  const transformFlowByPhaseData = (data: any): CumulativeFlowDataPoint[] => {
    if (!data || typeof data !== 'object') {
      return [];
    }

    const transformed = Object.entries(data).map(([date, value]: [string, any]) => {
      return {
        date,
        // Store both tasks and points for each phase
        tasks_backlog: value.tasks_backlog ?? 0,
        points_backlog: value.points_backlog ?? 0,
        tasks_wip: value.tasks_wip ?? 0,
        points_wip: value.points_wip ?? 0,
        tasks_review: value.tasks_review ?? 0,
        points_review: value.points_review ?? 0,
        tasks_test: value.tasks_test ?? 0,
        points_test: value.points_test ?? 0,
        tasks_done: value.tasks_done ?? 0,
        points_done: value.points_done ?? 0,
        tasks_blocked: value.tasks_blocked ?? 0,
        points_blocked: value.points_blocked ?? 0,
        tasks_deployable: value.tasks_deployable ?? 0,
        points_deployable: value.points_deployable ?? 0,
        // Backward compatibility - use tasks as default
        backlog: value.tasks_backlog ?? value.points_backlog ?? 0,
        wip: value.tasks_wip ?? value.points_wip ?? 0,
        review: value.tasks_review ?? value.points_review ?? 0,
        test: value.tasks_test ?? value.points_test ?? 0,
        done: value.tasks_done ?? value.points_done ?? 0,
        blocked: value.tasks_blocked ?? value.points_blocked ?? 0,
        deployable: value.tasks_deployable ?? value.points_deployable ?? 0,
      };
    });
    return transformed;
  };

  // Process data if available
  const allData: WidgetDataPoint[] = [];
  const sprintData: SprintData[] = [];

  queries.forEach((query, queryIndex) => {
    // Extract objectId and measure from the corresponding queryConfig
    // Match by index since queries are returned in the same order as queryConfigs
    const queryConfig = queryConfigs[queryIndex];
    let objectId = componentData.object_ids[0];
    let measure = primaryMeasure;

    if (queryConfig && queryConfig.queryKey) {
      const queryKey = queryConfig.queryKey;

      // Try to extract measure from queryKey
      if (componentData.object_type === 'sprint') {
        // For sprint: ['widget-measure', objectId, objectType, measure, nSprints]
        if (queryKey[3]) {
          measure = queryKey[3] as string;
          objectId = queryKey[1] as string;
        }
      } else {
        // For date range: ['widget-measure', objectId, measure, pageId, ...]
        if (queryKey[2]) {
          measure = queryKey[2] as string;
          objectId = queryKey[1] as string;
        }
      }
    }

    // Handle special sprint measures that fetch work periods and measure data
    if (isSpecialSprintMeasure && query.data && (query.data as any).sprints) {
      const responseData = query.data as any;
      const sprints = responseData.sprints as Array<{ workPeriod: WorkPeriod; data: any }>;

      sprints.forEach(({ workPeriod, data }) => {
        const measureData = data[primaryMeasure as Measure];
        if (measureData && typeof measureData === 'object' && Object.keys(measureData).length > 0) {
          // Transform the data into chart format
          let transformedData: SprintChartDataPoint[] | CumulativeFlowDataPoint[] = [];

          switch (primaryMeasure) {
            case Measure.Burns:
              transformedData = transformBurnsData(measureData);
              break;
            case Measure.FlowByPace:
              transformedData = transformFlowByPaceData(measureData);
              break;
            case Measure.FlowByPhase:
              transformedData = transformFlowByPhaseData(measureData);
              break;
          }

          // Store sprint data — forward the per-workflow `phases` companion
          // array (when present) so the Recharts cumulative-flow widget can
          // render override labels in legend/tooltip.
          sprintData.push({
            sprintId: workPeriod.id,
            sprintName: workPeriod.name,
            startDate: workPeriod.start_date,
            endDate: workPeriod.end_date || workPeriod.start_date,
            data: transformedData,
            phases: data?.phases,
          });

          // Also add aggregated data point for backward compatibility
          const values = Object.values(measureData).filter((v): v is number => typeof v === 'number');
          const avgValue = values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
          const objectName = getObjectName(objectId, componentData.object_type);
          // Only append measure title if there are multiple measures. Read the
          // measure from queryConfig.meta (set explicitly when the query config
          // was created) rather than a value derived from queryKey position,
          // since special sprint queries store nSprints at that same queryKey
          // index instead of the measure.
          const displayMeasure =
            typeof queryConfig?.meta?.measure === 'string' ? queryConfig.meta.measure : primaryMeasure;
          const displayName =
            measures.length > 1 ? `${objectName} - ${getMeasureTitle(displayMeasure)}` : workPeriod.name;
          allData.push({
            date: workPeriod.name,
            value: avgValue,
            // For multiple measures, use a consistent objectId per measure (not per sprint)
            // This allows consolidation by date to work properly
            objectId: measures.length > 1 ? measure : workPeriod.id,
            objectName: displayName,
            parentId: objectId,
            measure, // Add measure field for multi-measure support
          });
        }
      });
    } else if (isCustomSprintMeasureDataResponse(query.data)) {
      const responsePhases = (query.data as { phases?: PhaseLabel[] }).phases;
      if (isSpecialSprintMeasure) {
        // This shouldn't happen anymore for special measures, but keep for safety
        Object.entries(query.data)
          .filter(([entityId]) => entityId !== 'phases')
          .sort(
            (a, b) =>
              new Date((a[1] as { work_period: { end_date: string } }).work_period.end_date).getTime() -
              new Date((b[1] as { work_period: { end_date: string } }).work_period.end_date).getTime(),
          )
          .forEach(([entityId, measureDataEntry]) => {
            const measureData = measureDataEntry as (typeof query.data)[string];
            const data = measureData.measures[componentData.measure];
            if (data && Object.keys(data).length > 0) {
              let transformedData: SprintChartDataPoint[] | CumulativeFlowDataPoint[] = [];

              switch (componentData.measure) {
                case Measure.Burns:
                  transformedData = transformBurnsData(data);
                  break;
                case Measure.FlowByPace:
                  transformedData = transformFlowByPaceData(data);
                  break;
                case Measure.FlowByPhase:
                  transformedData = transformFlowByPhaseData(data);
                  break;
              }

              sprintData.push({
                sprintId: entityId,
                sprintName: measureData.work_period.name,
                startDate: measureData.work_period.start_date,
                endDate: measureData.work_period.end_date,
                data: transformedData,
                phases: responsePhases,
              });

              // Also add to allData for backward compatibility
              const lastNotNull = getLastNotNullValue(data);
              const objectName = getObjectName(objectId, componentData.object_type);
              // Only append measure title if there are multiple measures. Read the
              // measure from queryConfig.meta (set explicitly when the query config
              // was created) rather than a value derived from queryKey position,
              // since special sprint queries store nSprints at that same queryKey
              // index instead of the measure.
              const displayMeasure =
                typeof queryConfig?.meta?.measure === 'string' ? queryConfig.meta.measure : primaryMeasure;
              const displayName =
                measures.length > 1
                  ? `${objectName} - ${getMeasureTitle(displayMeasure)}`
                  : measureData.work_period.name;
              allData.push({
                date: measureData.work_period.name,
                value: lastNotNull || 0,
                // For multiple measures, use a consistent objectId per measure (not per sprint)
                // This allows consolidation by date to work properly
                objectId: measures.length > 1 ? measure : entityId,
                objectName: displayName,
                parentId: objectId,
                measure, // Add measure field for multi-measure support
              });
            }
          });
      } else {
        // For regular sprint measures, use existing logic
        Object.entries(query.data)
          .filter(([entityId]) => entityId !== 'phases')
          .sort(
            (a, b) =>
              new Date((a[1] as { work_period: { end_date: string } }).work_period.end_date).getTime() -
              new Date((b[1] as { work_period: { end_date: string } }).work_period.end_date).getTime(),
          )
          .forEach(([entityId, measureDataEntry]) => {
            const measureData = measureDataEntry as (typeof query.data)[string];
            const data = measureData.measures[measure]; // Use measure from query meta, not componentData.measure
            if (data && Object.keys(data).length > 0) {
              const lastNotNull = getLastNotNullValue(data);
              const objectName = getObjectName(objectId, componentData.object_type);
              // Only append measure title if there are multiple measures
              const displayName =
                measures.length > 1 ? `${objectName} - ${getMeasureTitle(measure)}` : measureData.work_period.name;
              allData.push({
                date: measureData.work_period.name,
                value: lastNotNull || 0,
                // For multiple measures, use a consistent objectId per measure (not per sprint)
                // This allows consolidation by date to work properly
                objectId: measures.length > 1 ? measure : entityId,
                objectName: displayName,
                parentId: objectId,
                measure, // Add measure field for multi-measure support
              });
            }
          });
      }
    } else if (query.data && measure) {
      // Process the measure for this specific query
      const measureData = (query.data as any)[measure as Measure];
      if (measureData && typeof measureData === 'object' && Object.keys(measureData).length > 0) {
        const objectName = getObjectName(objectId, componentData.object_type);
        // Only append measure title if there are multiple measures
        const displayName = measures.length > 1 ? `${objectName} - ${getMeasureTitle(measure)}` : objectName;
        const transformedData = Object.entries(measureData).map(([date, value]) => ({
          date,
          value: typeof value === 'number' ? value : 0,
          // For multiple measures, use the measure as objectId for proper consolidation by date
          objectId: measures.length > 1 ? measure : objectId,
          objectName: displayName,
          measure,
        }));
        allData.push(...transformedData);
      }
    }
  });

  // Process data for different widget types
  const tableData: TableWidgetDataPoint[] = allData.map(({ date, value, objectId, objectName }) => ({
    id: objectId,
    name: objectName,
    date,
    value,
  }));

  // Calculate averages and totals for tile view
  const total = allData.filter((item) => item.value !== 0).reduce((sum, item) => sum + item.value, 0);
  const average = allData.length > 0 ? total / allData.length : 0;

  return {
    chartData: allData,
    tableData,
    tileData: { average, total },
    sprintData: sprintData.length > 0 ? sprintData : undefined,
    measureUnits: _measureUnits,
    isLoading,
    error,
  };
};

/**
 * Hook to fetch and manage widget targets data
 *
 * @param {ComponentData} componentData - The component configuration data:
 * @returns {data: GetAvailableTargetsResponse | undefined, query: UseQueryResult} Object containing:
 *   - data: The fetched targets data response
 *   - query: The query result object with loading/error states
 */
const useWidgetTargets = (
  componentData: ComponentData,
): { data: GetAvailableTargetsResponse | undefined; query: UseQueryResult } => {
  const organizationId = useOrganizationId();
  const { startDate, endDate } = getComponentDateRange(componentData);

  // Get individual measures from the comma-separated string
  const measures = componentData.measure ? componentData.measure.split(',').map((m) => m.trim()) : [];

  const payload: GetAvailableTargetsPayload = {
    organization_id: organizationId || '',
    start_date: startDate,
    end_date: endDate,
    time_allocation_type: determineTimeAllocation(
      componentData.time_allocation_type as TimeAllocationType,
      startDate,
      endDate,
    ),
    // For multi-measure widgets, don't filter by measure - show all targets for the objects
    // For single-measure widgets, filter by the specific measure
    measure: measures.length > 1 ? undefined : componentData.measure,
  };

  switch (componentData.object_type) {
    case 'portfolio':
      payload.portfolio_ids = componentData.object_ids.join(',');
      break;
    case 'project':
      payload.project_ids = componentData.object_ids.join(',');
      break;
    case 'board':
      payload.subproject_ids = componentData.object_ids.join(',');
      break;
    case 'initiative':
      payload.initiative_ids = componentData.object_ids.join(',');
      break;
    case 'sprint':
      payload.subproject_ids = componentData.object_ids.join(',');
      payload.is_work_period_target = true;
      break;
  }

  const { data, query } = useAvailableTargets({ payload });

  return { data, query };
};

/**
 * Hook to convert component data into a target format
 *
 * @param {ComponentData} componentData - The component configuration data:
 * @returns {Partial<Target>} Partial target object containing:
 */
const useComponentDataAsTarget = (componentData: ComponentData): Partial<Target> => {
  const organizationId = useOrganizationId();
  const portfolios = useGlobalStore((state) => state.portfolios).filter((p) => p.organization?.id === organizationId);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const teams = useGlobalStore((state) => state.teams);
  const target: Partial<Target> = {
    measure: componentData.measure,
    target_comparison: TargetComparison.EQ,
    use_transform: true,
  };
  const { initiatives = [] } = useInitiatives(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<Initiative[]>);

  switch (componentData.object_type) {
    case 'portfolio':
      target.portfolios = portfolios
        .filter((p) => componentData.object_ids.includes(p.id))
        .map((p) => ({
          id: p.id,
          name: p.name,
        }));
      break;
    case 'project':
      target.projects = teams
        .filter((t) => componentData.object_ids.includes(t.id))
        .map((t) => ({
          id: t.id,
          name: t.name,
        }));
      break;
    case 'board':
      target.subprojects = teams.flatMap((t) =>
        t.subprojects
          .filter((s) => componentData.object_ids.includes(s.id))
          .map((s) => ({
            id: s.id,
            name: s.name,
          })),
      );
      break;
    case 'sprint':
      target.subprojects = teams.flatMap((t) =>
        t.subprojects
          .filter((s) => componentData.object_ids.includes(s.id))
          .map((s) => ({
            id: s.id,
            name: s.name,
          })),
      );
      target.is_work_period_target = true;
      break;
    case 'initiative':
      target.initiatives = initiatives
        .filter((i) => componentData.object_ids.includes(i.id))
        .map((i) => ({
          id: i.id,
          name: i.name,
          start_date: i.start_date,
          end_date: i.end_date,
        }));
  }

  return target;
};

/**
 * Hook to fetch available measures for a widget
 *
 * @param {WidgetFormData} formData - The form data containing widget configuration
 * @returns {Object} Object containing:
 *   - data: The available measures data
 *   - query: The query result object with loading/error states
 */
const useWidgetAvailableMeasures = (
  formData: WidgetFormData,
): { data: MeasureMetadata[] | undefined; query: QueryObserverResult<AvailableMeasuresResponse> } => {
  const isSprint = formData.objectType === 'sprint';
  const scope = isSprint ? [ModuleType.WorkPeriod] : [ModuleType.ProcessAnalysis, ModuleType.Dashboard];
  const { data, query } = useAvailableMeasures({
    payload: {
      scope,
      portfolio_id: formData.objectType === 'portfolio' ? formData.objects[0] : undefined,
      project_id: formData.objectType === 'project' ? formData.objects[0] : undefined,
      subproject_id: formData.objectType === 'board' || isSprint ? formData.objects[0] : undefined,
    },
    options: {
      enabled: !!formData.objectType && formData.objects.length === 1,
      staleTime: Infinity,
      gcTime: 1000 * 60 * 30,
      queryKey: ['available-measures', formData.objectType, formData.objects[0], scope],
    },
  });
  const allMeasures: MeasureMetadata[] = [];
  for (const s of scope) {
    let measures = data?.[s]?.measures;

    if (isSprint) {
      // For sprints, include custom measures AND the special chart measures (burns, flow_by_pace, flow_by_phase)
      measures = measures?.filter(
        (m) =>
          m.is_custom === true ||
          m.measure_name === Measure.Burns ||
          m.measure_name === Measure.FlowByPace ||
          m.measure_name === Measure.FlowByPhase,
      );
    }
    if (measures) {
      allMeasures.push(...measures);
    }
  }

  return { data: allMeasures, query };
};

export { useComponentDataAsTarget, useWidgetAvailableMeasures, useWidgetData, useWidgetTargets };
